import dayjs from 'dayjs';
import { type IOrderListItem } from '@/types/IOrder';
import { formatClientFullName } from './formatClientFullName';
import { maskFormatedId } from './maskFormatedId';
import { getPaymentStatusLabel } from './comparison';
import { getOrderNumberLabel } from './getOrderSequence';
import { localizedOrderStatuses } from '@/types/OrderStatusesEnum';

export interface ICallOrderSummary {
  uuid: string;
  /** Путь к заказу — открывается в новой вкладке. */
  orderPath: string;
  /** Сокращённый номер заказа, как в таблице заказов. */
  number: string;
  /** Полный номер — в подсказку. */
  fullNumber: string;
  date: string;
  status: string;
  paymentStatus: string;
  /** Клиент, на чей аккаунт оформлен заказ. */
  clientName: string;
  /** Заказ оформлен на аккаунт звонящего — тот же заказ есть в его заказах. */
  isClientOrder: boolean;
}

const UNKNOWN_DATE = 'Дата неизвестна';
const NO_PAYMENT_STATUS = 'Не указан';
const UNKNOWN_CLIENT = 'Аккаунт не указан';

/**
 * Совпадает ли владелец заказа с учёткой звонящего. Ключ строкой — `uuid`,
 * числом — `id` прежнего бэкенда (PAR-5189).
 */
const isOwnedBy = (
  order: IOrderListItem,
  accountKey: string | number | null
): boolean => {
  if (accountKey === null || order.client == null) return false;

  return typeof accountKey === 'string'
    ? order.client.uuid === accountKey
    : order.client.id === accountKey;
};

/**
 * Готовит строку таблицы заказов для модалки звонка. Чистая функция:
 * переводит заказ в набор готовых к рендеру строк, чтобы таблица оставалась
 * «тупой». Формулировки и формат совпадают с основной таблицей заказов.
 *
 * В выборке по номеру получателя заказы приходят с чужих аккаунтов, поэтому
 * строка несёт имя клиента и признак совпадения с аккаунтом звонящего: такой
 * заказ виден и во вкладке заказов клиента.
 *
 * Суммы нет с PAR-5189: выдача v2 не несёт позиций, из которых её считали.
 *
 * @param order - заказ из выдачи `admin/v2/orders`
 * @param accountKey - `uuid` (или `id`) аккаунта звонящего либо `null`
 * @returns данные строки таблицы
 */
export const getCallOrderSummary = (
  order: IOrderListItem,
  accountKey: string | number | null
): ICallOrderSummary => {
  const createdAt = dayjs(order.created_at);
  const clientName = formatClientFullName(order.client);

  return {
    uuid: order.uuid,
    orderPath: `/order/${order.uuid}`,
    number: maskFormatedId(order.formated_id),
    fullNumber: getOrderNumberLabel(order),
    date: createdAt.isValid()
      ? createdAt.format('DD.MM.YYYY HH:mm')
      : UNKNOWN_DATE,
    status: localizedOrderStatuses[order.status] ?? order.status,
    paymentStatus:
      getPaymentStatusLabel(order.payment_status) ?? NO_PAYMENT_STATUS,
    clientName: clientName === '' ? UNKNOWN_CLIENT : clientName,
    isClientOrder: isOwnedBy(order, accountKey)
  };
};
